import { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Link,
  Typography,
  Button,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LoadingButton from "../utils/LoadingButton";
import { ReactComponent as AndroidMenuIcon } from "./../../assets/android-menu.svg";
import { ReactComponent as IosSettingsIcon } from "./../../assets/ios-settings.svg";

// Components
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import { useTranslation, Trans } from "react-i18next";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

export default function QrCode({
  open,
  onClose,
  profileId,
  assignedServer,
  whatsAppSessionStatus,
  phoneNumber,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState(null);
  const [isQrCodeView, setIsQrCodeView] = useState(false);

  // Components
  const { showNotification } = useNotification();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (
      open &&
      profileId &&
      assignedServer &&
      qrCode === null &&
      whatsAppSessionStatus === "Unlinked" &&
      !isLoading
    ) {
      if (isQrCodeView) {
        fetchQrCode();
      } else {
        fetchPairingCode();
      }
    }
  }, [
    qrCode,
    open,
    profileId,
    assignedServer,
    whatsAppSessionStatus,
    isLoading,
    isQrCodeView,
  ]);

  const fetchPairingCode = async () => {
    trackEvent("qrcode-fetch_pairing_code");
    setIsLoading(true);
    setQrCode(null);
    try {
      const response = await fetch(
        `https://getpairingcode-tiph5o4x3q-uc.a.run.app/?profileId=${profileId}&baseUrl=${assignedServer}&phoneNumber=${phoneNumber}`
      );
      const data = await response.json();
      if (data.success) {
        if (data.message === "session_connected") {
          showNotification(t("qrCode.sessionAlreadyConnected"), "warning");
          setQrCode("");
        } else {
          setQrCode(data.message);
        }
      } else {
        const errorMessage = data.message.includes("Evaluation failed: t")
          ? t("qrCode.tooManyRequestsError")
          : data.message;
        showNotification(
          `${t("qrCode.errorLoadingQr")}: ${errorMessage}`,
          "warning"
        );
        setQrCode("");
      }
    } catch (error) {
      showNotification(
        `${t("qrCode.errorLoadingQr")}: ${error.message}`,
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQrCode = async () => {
    trackEvent("qrcode-fetch_qr");
    setIsLoading(true);
    try {
      const response = await fetch(
        `https://getwhatsappqrcode-tiph5o4x3q-uc.a.run.app/?profileId=${profileId}&baseUrl=${assignedServer}`
      );
      if (response.headers.get("Content-Type").startsWith("image/png")) {
        const qrCodeBlob = await response.blob();
        const reader = new FileReader();
        reader.readAsDataURL(qrCodeBlob);
        reader.onloadend = () => {
          setQrCode(reader.result);
          setIsLoading(false);
        };
      } else {
        const data = await response.json();
        const messageMap = {
          session_connected: t("qrCode.sessionAlreadyConnected"),
        };

        showNotification(
          messageMap[data.message] ||
          `${t("qrCode.errorLoadingQr")}: ${data.message}`,
          "warning"
        );
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      showNotification(
        `${t("qrCode.errorLoadingQr")}: ${error.message}`,
        "error"
      );
    }
  };

  const handleToggleView = () => {
    if (isQrCodeView) {
      fetchPairingCode();
    } else {
      fetchQrCode();
    }
    setIsQrCodeView((prev) => !prev);
  };

  const handleDoneClick = async () => {
    // 1. Create a promise that resolves after 10 seconds
    const delayPromise = new Promise((resolve) => {
      // This function will be called after 10000 milliseconds (10 seconds)
      setTimeout(() => {
        resolve();
      }, 10000);
    });

    // 2. Wait for the 10-second delay to complete
    await delayPromise;

    // 3. After the delay, call the onClose function
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          {isQrCodeView
            ? t("qrCode.title")
            : `${t("qrCode.title")}: ${phoneNumber}`}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
          }}
        >
          {/* Instruction Section */}
          <Box
            sx={{
              flex: 1,
              textAlign: { xs: "center", md: "left" },
              mr: { md: 4 },
            }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t("qrCode.instructions")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <b>1.</b> {t("qrCode.step1")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <b>2.</b>{" "}
              <Trans
                i18nKey="qrCode.step2"
                components={{
                  bold: <b />,
                  icon: (
                    <span
                      style={{
                        backgroundColor: "#f0f0f0",
                        padding: "2px",
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <AndroidMenuIcon />
                    </span>
                  ),
                  icon2: (
                    <span
                      style={{
                        backgroundColor: "#f0f0f0",
                        padding: "2px",
                        borderRadius: "50%",
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      <IosSettingsIcon />
                    </span>
                  ),
                }}
              />
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <b>3.</b>{" "}
              <Trans i18nKey="qrCode.step3" components={{ bold: <b /> }} />
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <b>4.</b> {/* Conditional text for step 4 */}
              {isQrCodeView ? (
                t("qrCode.step4qrCode")
              ) : (
                <Trans
                  i18nKey="qrCode.step4pairingCode"
                  components={{ bold: <b /> }}
                />
              )}
            </Typography>
            {isMobile ? null : (
              <Link
                onClick={handleToggleView}
                sx={{
                  display: "block",
                  color: "green",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {isQrCodeView
                  ? t("qrCode.linkWithPairingCode")
                  : t("qrCode.linkWithQrCode")}
              </Link>
            )}
            <Alert severity="warning" sx={{ mt: 2, fontWeight: "bold" }}>
              {t("qrCode.warning")}
            </Alert>

            {/* Help Links */}
            <Link
              href="https://faq.whatsapp.com/1317564962315842"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                color: "green",
                textDecoration: "underline",
                mt: 2,
              }}
            >
              {t("qrCode.helpLink")}
            </Link>
          </Box>

          {/* Conditional Rendering for the Code Display */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              minWidth: 250,
              mt: { xs: 4, md: 0 },
            }}
          >
            {isLoading ? (
              <CircularProgress />
            ) : qrCode ? (
              isQrCodeView ? (
                <Box sx={{ position: "relative" }}>
                  <img
                    src={qrCode}
                    alt={t("qrCode.altText")}
                    style={{ width: 250, height: 250 }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    background: "#f5f5f5",
                    borderRadius: 3,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 1.5,
                    width: "100%",
                    mx: "auto",
                  }}
                >
                  {qrCode.split("").map((char, idx) => (
                    <>
                      <Box
                        key={idx}
                        sx={{
                          width: 34,
                          height: 34,
                          border: "2px solid #b0bec5",
                          borderRadius: 2,
                          background: "#fff",
                          mx: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 18,
                          fontWeight: "bold",
                          fontFamily: "monospace",
                          color: "#374151",
                          boxSizing: "border-box",
                        }}
                      >
                        {char}
                      </Box>
                      {idx === 3 && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: 18,
                            fontWeight: "bold",
                            mx: 1,
                            color: "#374151",
                          }}
                        >
                          -
                        </Box>
                      )}
                    </>
                  ))}
                </Box>
              )
            ) : (
              <Typography>{t("qrCode.noQrAvailable")}</Typography>
            )}
          </Box>
        </Box>

        {!isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: { xs: 2, sm: 0 },
            }}
          >
            <LoadingButton
              variant="contained"
              onClick={handleDoneClick}
              sx={{
                mr: { sm: 2 },
                mb: { xs: 2, sm: 0 },
                borderRadius: "8px",
                fontWeight: "bold",
                py: 1.5,
                px: 4,
                width: { xs: "100%", sm: "auto" },
                background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                color: "#141b2d",
                boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                "&:hover": { background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)", color: "#fff" }
              }}
            >
              {t("qrCode.done")}
            </LoadingButton>

            <Button
              variant="outlined"
              color="error"
              onClick={isQrCodeView ? fetchQrCode : fetchPairingCode}
              loading={isLoading ? 1 : 0}
              sx={{
                borderRadius: "8px",
                fontWeight: "bold",
                borderWidth: "2px",
                "&:hover": { borderWidth: "2px" },
                color: "#e2726e",
                borderColor: "#e2726e",
                py: 1.5,
                px: 4,
                width: { xs: "100%", sm: "auto" }
              }}
            >
              {t("qrCode.newQrCode")}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
